import { useCallback, useState, useEffect } from "react";

import {
    TextField, DatePicker, Checkbox, Autocomplete, AutocompleteSearchData, SelectData, Button, Typography, Select, AutocompleteMultipleSelect,
    ExpandablePanel, ExpandablePanelBody, ExpandablePanelHeader, Table, useTable, TableMetadata, TableProps, Modal, Label, ArrowButton, TableStateWithFilters, ColumnSortEnum, IconWrapper
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as DocumentApprovedIcon } from "@king-ict/eupisi-ui/components/Icon/DocumentApprovedIcon.svg";
import { ReactComponent as EditIcon } from "@king-ict/eupisi-ui/components/Icon/Edit.svg";
import { CodeBook, PagedResult } from "@king-ict/eupisi-ui/models";
import { nameof, codeBookToSelectData, hrDate } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import {
    searchInstitutionsCodeBook, searchClassificationMarksCodebook, searchProgramNamesV2, searchEducationSectors, getProgramRequestListAsync,
    takeIntoProcessing, createProgramRequestResolutions, DocumentUploadRequest, uploadDocument, getASOOUsers, getMZOUsers, assignAdministrator, editEnvelope
} from "../../api/nisooApi";
import { MasterDetailTable, MasterDetailTableProps, useMasterDetailTable } from "../../components/MasterDetailTable";
import { ModalResolution } from "../../components/ModalResolution";
import { useDidUpdateEffect } from "../../hooks";
import { DocumentType } from "../../models/Document";
import { InstitutionCodeBook } from "../../models/Institution";
import {
    NISOO2ProgramRequestListSearch, ProgramRequestEnvelopeListItem, ProgramRequestEnvelopeProgram, TakeIntoProcessingRequest, NISOO2ProgramRequestSort,
    ProgramRequest, PreviousPage, SelectableProgramRequestEnvelopeProgram, createSelectableProgramRequestEnvelopeProgram, ExecutionResolutionDocument,
    createResolutionDocument, AssignAdministratorRequest, EnvelopeClassificationRequest
} from "../../models/ProgramRequest";
import { CreateProgramRequestsResolutionDocumentsRequest } from "../../models/ProgramRequest/CreateProgramRequestsResolutionDocumentsRequest";
import { ProgramRequestStatus } from "../../models/ProgramRequest/ProgramRequestStatus";
import { ResolutionDocumentType } from "../../models/ProgramRequest/ResolutionDocumentType";
import { SortDirection } from "../../models/Shared";
import { AppUserRole } from "../../models/User";
import { RoleEnum } from "../../models/User/RoleEnum";
import { useGetProgramRequestTypesByUserQuery, useGetProgramRequestStatusesForProcessingByUserQuery, useGetProgramRequestStatusesByUserQuery } from "../../queries/codeBookQuery";
import { useGetUserSessionQuery } from "../../queries/userQuery";
import { programRequestDetailsUrl } from "../../routes/routes";
import { getProgramRequestStatusColor } from "../../utils/programRequestUtils";
import {
    canFetchAdministrators,
    fetchAdministratorsForRole,
} from "../ProgramRequest/utils/administratorUtils";
import styles from "./NISOO2ProgramRequestList.module.scss";


export function NISOO2ProgramRequestList() {
    const storageKey = "NISOO2ProgramRequestListProgramRequestSearch";
    const { t } = useTranslation();
    const history = useHistory();

    const createInitialProgramRequests = (): PagedResult<ProgramRequestEnvelopeListItem> => {
        return {
            currentPage: 1,
            firstRowOnPage: 0,
            lastRowOnPage: 0,
            pageCount: 0,
            pageSize: 10,
            rowCount: 0,
            results: [],
        }
    }

    const createInitialtableState = (): TableStateWithFilters<NISOO2ProgramRequestListSearch> => {
        const sessionStorageTableState = sessionStorage.getItem(storageKey);
        if (sessionStorageTableState === null) {
            return {
                institutions: [],
                classificationMark: '',
                programRequestStatusId: undefined,
                programName: '',
                programRequestTypeIds: [],
                educationSectors: [],
                administrator: '',
                dateFrom: undefined,
                dateTo: undefined,
                currentUserProgramRequests: true,
                programRequestsWithoutAdministrator: true,
                page: 1,
                pageSize: 10,
                sortBy: NISOO2ProgramRequestSort.EnvelopeDate,
                order: ColumnSortEnum.DESC
            };
        }

        var result = JSON.parse(sessionStorageTableState) as TableStateWithFilters<NISOO2ProgramRequestListSearch>;

        if (result.dateFrom) {
            result.dateFrom = new Date(result.dateFrom?.toString());
        }
        if (result.dateTo) {
            result.dateTo = new Date(result.dateTo.toString());
        }

        return result;
    }

    const createInitialProgramRequestSearch = (initialTableState: TableStateWithFilters<NISOO2ProgramRequestListSearch>): NISOO2ProgramRequestListSearch => {
        return {
            institutions: initialTableState.institutions,
            classificationMark: initialTableState.classificationMark,
            programRequestStatusId: initialTableState.programRequestStatusId,
            programName: initialTableState.programName,
            programRequestTypeIds: initialTableState.programRequestTypeIds,
            educationSectors: initialTableState.educationSectors,
            administrator: initialTableState.administrator,
            dateFrom: initialTableState.dateFrom,
            dateTo: initialTableState.dateTo,
            currentUserProgramRequests: initialTableState.currentUserProgramRequests,
            programRequestsWithoutAdministrator: initialTableState.programRequestsWithoutAdministrator
        }
    }

    const createDefaultProcessingRequest = (): TakeIntoProcessingRequest => {
        return {
            envelopeId: 0,
            classificationMark: ""
        };
    };

    const createDefaultEnvelopeClassificationRequest = (): EnvelopeClassificationRequest => {
        return {
            envelopeId: 0,
            classificationMark: "",
            registryNumber: "",
            registrationDate: new Date()
        };
    };

    const createDefaultAssignAdministratorRequest = (): AssignAdministratorRequest => {
        return {
            envelopeId: 0,
            administratorId: undefined,
        };
    };

    const createResolutionModalCaptions = (): string[] => {
        return [t("request.expertOpinionDocuments"), t("request.expertOpinionClassificationMark"), t("request.expertOpinionRegisterNumber"), t("request.expertOpinionIssueDate")];
    };

    const handleInstitutionsSearch = useCallback(
        async (search: AutocompleteSearchData) => {
            if (search.text) {
                const institutions = await searchInstitutionsCodeBook(search.text, undefined, undefined);
                return {
                    valueProp: "id",
                    textProp: "name",
                    data: institutions
                } as SelectData<InstitutionCodeBook>;
            }
            return {
                valueProp: "id",
                textProp: "name"
            } as SelectData<InstitutionCodeBook>;
        }, []

    );

    const handleInstitutionFilterChange = async (filter: string) => {
        var result = await searchInstitutionsCodeBook(filter, undefined, undefined);
        setFilteredInstitutions(result);
    };

    const handleClassificationMarksSearch = useCallback(
        async (search: AutocompleteSearchData) => {
            if (search.text) {
                const classificationMarks = await searchClassificationMarksCodebook(search.text);
                return {
                    valueProp: "id",
                    textProp: "name",
                    data: classificationMarks
                } as SelectData<CodeBook>;
            }
            return {
                valueProp: "id",
                textProp: "name"
            } as SelectData<CodeBook>;
        }, []
    );

    const handleInstitutionChangeV2 = (e: any) => {
        const newInstitutions = e.target.value as InstitutionCodeBook[];
        const newNISOO2ProgramRequestListSearch: NISOO2ProgramRequestListSearch = {
            ...programRequestSearch,
            institutions: newInstitutions.slice(0, 1)
        };
        setProgramRequestSearch(newNISOO2ProgramRequestListSearch);
    };

    const handleClassificationMarkChange = (e: any) => {
        const classificationMark = e.target.value;
        const newNISOO2ProgramRequestListSearch: NISOO2ProgramRequestListSearch = {
            ...programRequestSearch,
            classificationMark: classificationMark
        };

        setProgramRequestSearch(newNISOO2ProgramRequestListSearch);
    };

    const handleProgramNameChange = (e: any) => {
        const programName = e.target.value;
        const newNISOO2ProgramrequestListSearch: NISOO2ProgramRequestListSearch = {
            ...programRequestSearch,
            programName: programName
        };
        setProgramRequestSearch(newNISOO2ProgramrequestListSearch);
    };

    const handleProgramRequestStatusChange = (e: any) => {
        const programRequestStatusId = Number.parseInt(e.target.value);
        const newNISOO2ProgramRequestListSearch: NISOO2ProgramRequestListSearch = {
            ...programRequestSearch,
            programRequestStatusId: programRequestStatusId
        };
        setProgramRequestSearch(newNISOO2ProgramRequestListSearch);
    };

    const handleProgramRequestTypeChange = (e: any) => {
        const newProgramRequestTypeIds = e.target.value as number[];
        const newNISOO2ProgramRequestListSearch: NISOO2ProgramRequestListSearch = {
            ...programRequestSearch,
            programRequestTypeIds: newProgramRequestTypeIds
        };
        setProgramRequestSearch(newNISOO2ProgramRequestListSearch);
    };

    const handleEducationSectorChange = (e: any) => {
        const newEducationSectors = e.target.value as CodeBook[];
        const newNISOO2ProgramRequestListSearch: NISOO2ProgramRequestListSearch = {
            ...programRequestSearch,
            educationSectors: newEducationSectors.slice(0, 10)
        };
        setProgramRequestSearch(newNISOO2ProgramRequestListSearch);

    };

    const handleAdministratorFilterChange = (e: any) => {
        const newAdministrator = e.target.value as string;
        const newNISOO2ProgramRequestListSearch: NISOO2ProgramRequestListSearch = {
            ...programRequestSearch,
            administrator: newAdministrator
        };
        setProgramRequestSearch(newNISOO2ProgramRequestListSearch);
    };

    const handleDateFromFilterChange = (e: any) => {
        let newDateFrom = undefined;
        if (e.target.value) {
            newDateFrom = e.target.value as Date;
            newDateFrom.setHours(0);
            newDateFrom.setMinutes(0);
            newDateFrom.setSeconds(0);
            newDateFrom.setMilliseconds(0);
        }
        const newNISOO2ProgramRequestListSearch: NISOO2ProgramRequestListSearch = {
            ...programRequestSearch,
            dateFrom: newDateFrom
        };
        setProgramRequestSearch(newNISOO2ProgramRequestListSearch);
    };

    const handleDateToFilterChange = (e: any) => {
        let newDateTo = undefined;
        if (e.target.value) {
            newDateTo = e.target.value as Date;
            newDateTo.setHours(0);
            newDateTo.setMinutes(0);
            newDateTo.setSeconds(0);
            newDateTo.setMilliseconds(0);
        }
        const newNISOO2ProgramRequestListSearch: NISOO2ProgramRequestListSearch = {
            ...programRequestSearch,
            dateTo: newDateTo
        };
        setProgramRequestSearch(newNISOO2ProgramRequestListSearch);
    };

    const handleCurrentUserProgramRequestChange = (e: any) => {
        const currentUserChecked = e.target.checked as boolean;
        const newNISOO2ProgramRequestListSearch: NISOO2ProgramRequestListSearch = {
            ...programRequestSearch,
            currentUserProgramRequests: currentUserChecked
        };
        setProgramRequestSearch(newNISOO2ProgramRequestListSearch);
    };

    const handleProgramRequestWithoutAdministratorChange = (e: any) => {
        const programRequestWithoutAdminChecked = e.target.checked as boolean;
        const newNISOO2ProgramRequestListSearch: NISOO2ProgramRequestListSearch = {
            ...programRequestSearch,
            programRequestsWithoutAdministrator: programRequestWithoutAdminChecked
        };
        setProgramRequestSearch(newNISOO2ProgramRequestListSearch);
    };

    const handleEducationSectorFilterChange = async (filter: string) => {
        var result = await searchEducationSectors(filter);
        setFilteredEducationSectors(result);
    };

    const onTakeIntoProcessingClick = (envelopeID: number) => {
        setClickedEnvelopeID(envelopeID);

        const newProcessingRequest: TakeIntoProcessingRequest = {
            ...processingRequest,
            envelopeId: envelopeID
        };
        setProcessingRequest(newProcessingRequest);
        setIsTakeIntoProcessingModalOpened(true);
    };


    const onAssignAdministratorClick = (envelopeID: number) => {
        setClickedEnvelopeID(envelopeID);

        const newAssignAdministratorRequest: AssignAdministratorRequest = {
            ...assignAdministratorRequest,
            envelopeId: envelopeID
        };

        setAssignAdministratorRequest(newAssignAdministratorRequest);
        setIsAssignAdministratorModalOpened(true);
    };


    const onGroupOpinionClick = (programsForOpinion: ProgramRequestEnvelopeProgram[], documentType: ResolutionDocumentType) => {
        const convertedData = programsForOpinion.map(data => { return createSelectableProgramRequestEnvelopeProgram(data) });
        setSelectedPrograms(convertedData);

        const newResolutionDocument: ExecutionResolutionDocument = {
            ...resolutionDocument,
            documentTypeId: documentType,
        };

        setResolutionDocument(newResolutionDocument);

        setIsGroupOpinionModalOpened(true);
    };

    const classificationMarkRegistryNumberEditClick = (envelopeId: number, classificationMark: string, registryNumber: string, registrationDate: Date) => {

        const newEnvelopeClassificationRequest: EnvelopeClassificationRequest = {
            envelopeId: envelopeId,
            classificationMark: classificationMark,
            registryNumber: registryNumber,
            registrationDate: registrationDate
        };

        setEnvelopeClassificationRequest(newEnvelopeClassificationRequest);
        setIsClassificationMarkRegistryNumberEditModalOpened(true);
    };

    const handleToggleCancelTakeIntoProcessingModal = () => {
        setProcessingRequest(createDefaultProcessingRequest);
        setIsTakeIntoProcessingModalOpened((prev) => !prev);
    };

    const handleToggleCancelAssignAdministratorModal = () => {
        setAssignAdministratorRequest(createDefaultAssignAdministratorRequest);
        setIsAssignAdministratorModalOpened((prev) => !prev);
    };

    const handleToggleConfirmAssignAdministratorModal = () => {
        if (assignAdministratorRequest.administratorId === undefined) {
            return;
        }

        setIsConfirmAssignAdministratorModalOpened(true);
    };

    const handleCancelConfirmAssignAdministratorModal = () => {
        setIsConfirmAssignAdministratorModalOpened(false);
    };

    const handleConfirmAssignAdministratorModal = async () => {
        await assignAdministrator(assignAdministratorRequest);

        search();

        setIsConfirmAssignAdministratorModalOpened(false);
        setIsAssignAdministratorModalOpened(false);
    };

    const handleToggleConfirmTakeIntoProcessingModal = async () => {
        if (processingRequest.classificationMark.trim() === "" || processingRequest.classificationMark === undefined) {
            return;
        }
        await takeIntoProcessing(processingRequest);

        //možda staviti modal sa porukom da je sve ok

        search();

        setIsTakeIntoProcessingModalOpened((prev) => !prev);
    };

    const handleToggleCancelIsGroupOpinionModal = () => {
        setIsGroupOpinionModalOpened((prev) => !prev);
    };

    const handleToggleCancelClassificationMarkRegistryNumberEditModal = () => {
        setEnvelopeClassificationRequest(createDefaultEnvelopeClassificationRequest);
        setIsClassificationMarkRegistryNumberEditModalOpened((prev) => !prev);
    };

    const handleConfirmModalClose = () => {
        setConfirmClassificationMarkEditModalOpened(false);
    };

    const handleConfirmModalConfirm = async () => {

        await editEnvelope(envelopeClassificationRequest).then(() => {
            setOpenSuccessModal(true);
        }
        );

    };

    const handleCloseSuccessModal = () => {
        setOpenSuccessModal(false);
        setConfirmClassificationMarkEditModalOpened(false);
        setIsClassificationMarkRegistryNumberEditModalOpened(false);
        search();
    };

    const handleGroupOpinionValidationModalClose = () => {
        setIsGroupOpinionValidationModalOpened(false);
    };

    const handleCloseResolutionModal = () => {
        setOpenResolutionModal(false);
        setIsGroupOpinionModalOpened(true);
    };

    const handleStatusChangeMessageModal = () => {
        setOpenStatusChangeModal(false);
        setOpenResolutionConfirmModal(false);
        setOpenResolutionModal(false);
        setIsGroupOpinionModalOpened(false);
        search();
    };

    const handleCancelResolutionConfirmModal = () => {
        setOpenResolutionConfirmModal(false);
    };

    const handleConfirmResolutionConfirmModal = async () => {
        await uploadResolutionDocument(tmpResolution);

        var createProgramRequestsResolutionDocumentsRequest: CreateProgramRequestsResolutionDocumentsRequest = {
            programRequestIds: selectedPrograms.filter(p => p.isSelected === true).map(p => p.programRequestId),
            action: tmpApproved ? 1 : 2,
            documentId: tmpResolution.resolutionDocument?.id!,
            documentTypeId: tmpResolution.documentTypeId,
            classificationMark: tmpResolution.classificationMark!,
            registryNumber: tmpResolution.registryNumber,
            issueDate: tmpResolution.issueDate!,
            description: tmpResolution.description!,
            processingDeadline: tmpResolution.processingDeadline
        }

        if (tmpApproved) {
            setConfirmationMessage(tmpResolution.documentTypeId === ResolutionDocumentType.InstitutionBasicDataChangeResolution
                ? t("request.approveGroupResolutionMessage") : t("request.approveGroupExpertOpinionMessage"));
        }
        else {
            setConfirmationMessage(tmpResolution.documentTypeId === ResolutionDocumentType.InstitutionBasicDataChangeResolution
                ? t("request.rejectGroupResolutionMessage") : t("request.cancelGroupExpertOpinionMessage"));
        }

        await createProgramRequestResolutions(createProgramRequestsResolutionDocumentsRequest).then(() => {
            setOpenStatusChangeModal(true);
        });

    };

    const getResolutionModalCaptions = (documentType: ResolutionDocumentType): string[] => {
        switch (documentType) {
            case ResolutionDocumentType.ExpertOpinion:
                return [t("request.expertOpinionDocuments"), t("request.expertOpinionClassificationMark"), t("request.expertOpinionRegisterNumber"), t("request.expertOpinionIssueDate")];
            case ResolutionDocumentType.InstitutionBasicDataChangeResolution:
                return [t("request.executionResolutionModalCaption"), t("request.executionResolutionClassificationMark"), t("request.executionResolutionRegisterNumber"), t("request.executionResolutionIssueDate")];
            default:
                return [];
        }
    }

    const handleConfirmedResolutionModal = async (approved: boolean, resolution: ExecutionResolutionDocument) => {

        if (selectedPrograms && selectedPrograms.filter(p => p.isSelected === true).length >= 2) {

            if (approved) {
                resolution.documentTypeId === ResolutionDocumentType.InstitutionBasicDataChangeResolution
                    ? setConfirmationModalMessage(t("request.executionGroupResolutionApprovedConfirmModalCaption")) : setConfirmationModalMessage(t("request.expertOpinionApprovedConfirmModalCaption"));
            }
            else {
                resolution.documentTypeId === ResolutionDocumentType.InstitutionBasicDataChangeResolution
                    ? setConfirmationModalMessage(t("request.executionGroupResolutionDeclinedConfirmModalCaption")) : setConfirmationModalMessage(t("request.expertOpinionDeclinedConfirmModalCaption"));
            }

            setTmpApproved(approved);
            setTmpResolution(resolution);

            setOpenResolutionConfirmModal(true);
        }
    };

    const uploadResolutionDocument = async (resolution: ExecutionResolutionDocument) => {
        if (resolution.resolutionDocument && resolution.resolutionDocument.id === -1) {
            var resolutionDocumentUploadRequest: DocumentUploadRequest = {
                document: resolution.resolutionDocument!.file!,
                documentTypeId: DocumentType.ProgramExecutionResolution
            };

            await uploadDocument(resolutionDocumentUploadRequest).then((idDoc) => {
                var resolutionDocument = resolution.resolutionDocument;
                resolutionDocument!.id = idDoc;
                setResolutionDocument((prev) => ({
                    ...prev,
                    resolutionDocument: resolutionDocument
                }));
            });
        }

    }

    const handleToggleConfirmIsGroupOpinionModal = () => {
        if (selectedPrograms.filter(p => p.isSelected === true).length < 2) {
            setIsGroupOpinionValidationModalOpened(true);
        }
        else {
            setIsGroupOpinionModalOpened((prev) => !prev);

            let classificationMark = selectedPrograms[0].classificationMark;

            var newResolutionDocument: ExecutionResolutionDocument = {
                ...resolutionDocument,
                classificationMark: classificationMark
            };

            setResolutionDocument(newResolutionDocument);

            setOpenResolutionModal(true);
        }
    };

    const handleToggleConfirmClassificationMarkRegistryNumberEditModal = () => {

        if (!envelopeClassificationRequest.classificationMark || !envelopeClassificationRequest.registryNumber || !envelopeClassificationRequest.registrationDate) {
            return;
        }

        //modal
        setConfirmClassificationMarkEditModalOpened(true);

    };

    const handleProcessingClassificationMarkChange = (e: any) => {
        const processingClassificationMark = e.target.value as string;
        const newProcessingRequest: TakeIntoProcessingRequest = {
            ...processingRequest,
            classificationMark: processingClassificationMark
        };
        setProcessingRequest(newProcessingRequest);
    };

    const handleItemSelectedChanged = (e: any) => {
        const id = Number.parseInt(e.target.value);
        const isChecked = e.target.checked as boolean;

        if (id) {
            const newSelectedPrograms = selectedPrograms.map(x => {
                if (x.programRequestId === id) {
                    return { ...x, isSelected: isChecked };
                }
                return x;
            });
            setSelectedPrograms(newSelectedPrograms);
        }
    };

    const handleEnvelopeClassificationMarkChange = (event: any) => {
        var classificationMark = event.target.value;

        const newEnvelopeClassificationRequest: EnvelopeClassificationRequest = {
            ...envelopeClassificationRequest,
            classificationMark: classificationMark
        };

        setEnvelopeClassificationRequest(newEnvelopeClassificationRequest);
    }

    const handleEnvelopeRegistryNumberChange = (event: any) => {
        var registryNumber = event.target.value;

        const newEnvelopeClassificationRequest: EnvelopeClassificationRequest = {
            ...envelopeClassificationRequest,
            registryNumber: registryNumber
        };

        setEnvelopeClassificationRequest(newEnvelopeClassificationRequest);
    }

    const handleEnvelopeRegistrationDateChange = (event: any) => {
        var registrationDate = event.target.value;

        const newEnvelopeClassificationRequest: EnvelopeClassificationRequest = {
            ...envelopeClassificationRequest,
            registrationDate: registrationDate
        };

        setEnvelopeClassificationRequest(newEnvelopeClassificationRequest);
    };

    const convertProgramRequestSearchToApiRequest = (programRequestSearch: NISOO2ProgramRequestListSearch) => {
        var request = {
            institutionId: (programRequestSearch.institutions && programRequestSearch.institutions.length > 0) ? programRequestSearch.institutions[0].id : undefined,
            programName: programRequestSearch.programName,
            dateFrom: programRequestSearch.dateFrom,
            dateTo: programRequestSearch.dateTo,
            classificationMark: programRequestSearch.classificationMark,
            programRequestStatusId: programRequestSearch.programRequestStatusId,
            programRequestTypeIds: programRequestSearch.programRequestTypeIds,
            educationSectorIds: programRequestSearch.educationSectors.map(es => es.id),
            administrator: programRequestSearch.administrator,
            currentUserProgramRequests: (userSession?.roleId === RoleEnum.ASOOOffice || userSession?.roleId === RoleEnum.MZOMOffice) ? false : programRequestSearch.currentUserProgramRequests,
            programRequestsWithoutAdministrator: (userSession?.roleId === RoleEnum.ASOO || userSession?.roleId === RoleEnum.MZOM || userSession?.roleId === RoleEnum.ASOOOffice || userSession?.roleId === RoleEnum.MZOMOffice) ? false : programRequestSearch.programRequestsWithoutAdministrator,
            sortBy: tableState.sortBy ?? NISOO2ProgramRequestSort.InstitutionName,
            sortDirection: tableState.order && tableState.order === ColumnSortEnum.ASC ? SortDirection.Asc : SortDirection.Desc,
            page: tableState.page ?? 1,
            pageSize: tableState.pageSize ?? 10
        };
        return request;
    };


    const search = async () => {
        setfilters(programRequestSearch);
    };

    const clearFilters = async () => {
        var emptySearch = {
            institutions: [],
            programName: "",
            classificationMark: "",
            programRequestTypeIds: [],
            educationSectors: [],
            administrator: "",
            currentUserProgramRequests: (userSession?.roleId === RoleEnum.ASOOOffice || userSession?.roleId === RoleEnum.MZOMOffice) ? false : true,
            programRequestsWithoutAdministrator: (userSession?.roleId === RoleEnum.ASOO || userSession?.roleId === RoleEnum.ASOOOffice || userSession?.roleId === RoleEnum.MZOM || userSession?.roleId === RoleEnum.MZOMOffice) ? false : true,
        };
        setProgramRequestSearch(emptySearch);
        setEducationSectors([]);
        setfilters(emptySearch);
    };

    const [initialTableState] = useState<TableStateWithFilters<NISOO2ProgramRequestListSearch>>(() => createInitialtableState());
    const [programRequestSearch, setProgramRequestSearch] = useState<NISOO2ProgramRequestListSearch>(createInitialProgramRequestSearch(initialTableState));
    const [filteredEducationSectors, setFilteredEducationSectors] = useState<CodeBook[]>([] as CodeBook[]);
    const [filteredInstitutions, setFilteredInstitutions] = useState<CodeBook[]>([] as CodeBook[]);
    const [filteredPrograms, setFilteredPrograms] = useState<CodeBook[]>([] as CodeBook[]);
    const [tableData, setTableData] = useState<PagedResult<ProgramRequestEnvelopeListItem>>(createInitialProgramRequests());
    const { data: userSession } = useGetUserSessionQuery();
    const { data: programRequestStatuses } = useGetProgramRequestStatusesByUserQuery(userSession?.roleId ?? 0);
    const { data: programRequestTypes } = useGetProgramRequestTypesByUserQuery(userSession?.roleId ?? 0);
    const [educationSectors, setEducationSectors] = useState<CodeBook[]>([] as CodeBook[]);
    const [isTakeIntoProcessingModalOpened, setIsTakeIntoProcessingModalOpened] = useState(false);
    const [isAssignAdministratorModalOpened, setIsAssignAdministratorModalOpened] = useState(false);
    const [isConfirmAssignAdministratorModalOpened, setIsConfirmAssignAdministratorModalOpened] = useState(false);
    const [clickedEnvelopeId, setClickedEnvelopeID] = useState<number>(0);
    const [processingRequest, setProcessingRequest] = useState<TakeIntoProcessingRequest>(createDefaultProcessingRequest);
    const [assignAdministratorRequest, setAssignAdministratorRequest] = useState<AssignAdministratorRequest>(createDefaultAssignAdministratorRequest);
    const [isGroupOpinionModalOpened, setIsGroupOpinionModalOpened] = useState(false);
    const [isGroupOpinionValidationModalOpened, setIsGroupOpinionValidationModalOpened] = useState(false);
    const [selectedPrograms, setSelectedPrograms] = useState<SelectableProgramRequestEnvelopeProgram[]>([]);
    const [openResolutionModal, setOpenResolutionModal] = useState(false);
    const [resolutionDocument, setResolutionDocument] = useState<ExecutionResolutionDocument>(createResolutionDocument(ResolutionDocumentType.ExpertOpinion, ""));
    const [resolutionModalCaptions] = useState<string[]>(createResolutionModalCaptions());
    const [openStatusChangeModal, setOpenStatusChangeModal] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState("");
    const [filteredAdministrators, setFilteredAdministrators] = useState<AppUserRole[]>([] as AppUserRole[]);
    const [administratorConfirmationMessage, setAdministratorConfirmationMessage] = useState("");
    const [isClassificationMarkRegistryNumberEditModalOpened, setIsClassificationMarkRegistryNumberEditModalOpened] = useState(false);
    const [envelopeClassificationRequest, setEnvelopeClassificationRequest] = useState<EnvelopeClassificationRequest>(createDefaultEnvelopeClassificationRequest);
    const [confirmClassificationMarkEditModalOpened, setConfirmClassificationMarkEditModalOpened] = useState(false);
    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [openResolutionConfirmModal, setOpenResolutionConfirmModal] = useState(false);
    const [confirmationModalMessage, setConfirmationModalMessage] = useState("");
    const [tmpResolution, setTmpResolution] = useState<ExecutionResolutionDocument>(resolutionDocument);
    const [tmpApproved, setTmpApproved] = useState<boolean>(false);


    const programRequestTableMetadata: TableMetadata<ProgramRequestEnvelopeListItem> = {
        rowKey: "envelopeId",
        columns: [
            {
                columnKey: "envelopeDate",
                header: t("common.programRequestDate"),
                accessor: (item: ProgramRequestEnvelopeListItem) =>
                    new Date(item.envelopeDate).toLocaleString("hr-HR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                    }),
                sortBy: NISOO2ProgramRequestSort.EnvelopeDate
            },
            {
                columnKey: "programRequestDate",
                header: t("common.programRequestCreateDate"),
                accessor: (item: ProgramRequestEnvelopeListItem) =>
                    new Date(item.programRequestDate).toLocaleString("hr-HR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                    }),
                sortBy: NISOO2ProgramRequestSort.ProgramRequestDate
            },
            {
                columnKey: "institutionName",
                header: t("institution.institutionName"),
                accessor: "institutionName",
                sortBy: NISOO2ProgramRequestSort.InstitutionName,
                color: "success"
            },
            {
                columnKey: "educationSectorName",
                header: t("codeBook.educationSector"),
                accessor: "educationSectorName",
                sortBy: NISOO2ProgramRequestSort.EducationSectorName
            },
            {
                columnKey: "programRequestTypeName",
                header: t("request.groupProgramRequestTypeTitle"),
                accessor: "programRequestTypeName",
                sortBy: NISOO2ProgramRequestSort.ProgramRequestTypeName
            },
            {
                columnKey: "groupOpinionASOO",
                header: "",
                accessor: (item) => {
                    if (item.receivedById !== null && userSession?.userId === item.receivedById && item.programs && item.programs.length >= 2
                        && item.programs.filter(p => p.isCurrent === true && p.statusId === ProgramRequestStatus.SubmittedASOO).length >= 2) {
                        return (<div className={"text-right"}>
                            <EditIcon
                                className={styles.deleteButton}
                                onClick={() => onGroupOpinionClick(item.programs.filter(p => p.isCurrent === true && p.statusId === ProgramRequestStatus.SubmittedASOO), ResolutionDocumentType.ExpertOpinion)}
                            />
                        </div>)
                    }
                }
            },
            {
                columnKey: "groupOpinionMZOM",
                header: "",
                accessor: (item) => {
                    if (item.receivedById !== null && userSession?.userId === item.receivedById
                        && (userSession?.roleId === RoleEnum.MZOM || userSession?.roleId === RoleEnum.MZOMAdmin)
                        && item.programs && item.programs.length >= 2
                        && item.programs.filter(p => p.isCurrent === true && p.statusId === ProgramRequestStatus.PausedMZOM).length >= 2) {
                        return (<div className={"text-right"}>
                            <EditIcon
                                className={styles.deleteButton}
                                onClick={() => onGroupOpinionClick(item.programs.filter(p => p.isCurrent === true && p.statusId === ProgramRequestStatus.PausedMZOM), ResolutionDocumentType.InstitutionBasicDataChangeResolution)}
                            />
                        </div>)
                    }
                }
            },
            {
                columnKey: "classificationMarkRegistryNumberEdit",
                header: "",
                accessor: (item) => {
                    if ((!item.programs[0].classificationMark || !item.registryNumber || !item.registrationDate) && (userSession?.roleId === RoleEnum.ASOOOffice || userSession?.roleId === RoleEnum.MZOMOffice)) {
                        return (<div className={"text-right"}>
                            <EditIcon
                                className={styles.deleteButton}
                                title={t("request.classificationMarkRegistryNumberEditTooltip")}
                                onClick={() => classificationMarkRegistryNumberEditClick(item.envelopeId, item.programs[0].classificationMark, item.registryNumber, item.registrationDate ? item.registrationDate : new Date())}
                            />
                        </div>)
                    }

                }
            },
            {
                columnKey: "receivedBy",
                header: t("request.administrator"),
                accessor: (item) => {
                    return (
                        <div>
                            {item.receivedBy &&
                                <Typography>{item.receivedBy}</Typography>
                            }
                            {item.receivedBy == null && (userSession?.roleId !== RoleEnum.ASOOAdmin && userSession?.roleId !== RoleEnum.MZOMAdmin
                                && userSession?.roleId !== RoleEnum.Helpdesk) &&
                                <Typography>--</Typography>
                            }
                            {item.receivedBy == null && !item.programs.some(p => p.isCurrent && (p.statusId === ProgramRequestStatus.SubmittedASOO
                                || p.statusId === ProgramRequestStatus.HarmonizationASOO || p.statusId === ProgramRequestStatus.CancelledASOO)) && userSession?.roleId === RoleEnum.ASOOAdmin &&
                                <Typography>--</Typography>
                            }
                            {item.receivedBy == null && !item.programs.some(p => p.isCurrent && (p.statusId === ProgramRequestStatus.SubmittedMZO
                                // || p.statusId === ProgramRequestStatus.HarmonizationMZO || p.statusId === ProgramRequestStatus.ConsentMZO))
                                || p.statusId === ProgramRequestStatus.ConsentMZO || p.statusId === ProgramRequestStatus.CancelledMZO || p.statusId === ProgramRequestStatus.PausedMZOM))
                                && userSession?.roleId === RoleEnum.MZOMAdmin &&
                                <Typography>--</Typography>
                            }
                            {item.receivedBy == null && !item.programs.some(p => p.isCurrent && (p.statusId === ProgramRequestStatus.SubmittedASOO
                                || p.statusId === ProgramRequestStatus.HarmonizationASOO || p.statusId === ProgramRequestStatus.CancelledASOO || p.statusId === ProgramRequestStatus.SubmittedMZO
                                // || p.statusId === ProgramRequestStatus.HarmonizationMZO || p.statusId === ProgramRequestStatus.ConsentMZO))
                                || p.statusId === ProgramRequestStatus.ConsentMZO || p.statusId === ProgramRequestStatus.CancelledMZO || p.statusId === ProgramRequestStatus.PausedMZOM))
                                && userSession?.roleId === RoleEnum.Helpdesk &&
                                <Typography>--</Typography>
                            }
                            {item.receivedBy == null && item.programs.some(p => p.isCurrent && (p.statusId === ProgramRequestStatus.SubmittedASOO
                                || p.statusId === ProgramRequestStatus.HarmonizationASOO || p.statusId === ProgramRequestStatus.CancelledASOO)) && userSession?.roleId === RoleEnum.ASOOAdmin &&
                                <Button outline className={styles.editButton} onClick={() => onAssignAdministratorClick(item.envelopeId)}>
                                    {t("request.assignAdministrator")}
                                </Button>
                            }
                            {item.receivedBy == null && item.programs.some(p => p.isCurrent && (p.statusId === ProgramRequestStatus.SubmittedMZO
                                // || p.statusId === ProgramRequestStatus.HarmonizationMZO || p.statusId === ProgramRequestStatus.ConsentMZO))
                                || p.statusId === ProgramRequestStatus.ConsentMZO || p.statusId === ProgramRequestStatus.CancelledMZO || p.statusId === ProgramRequestStatus.PausedMZOM))
                                && userSession?.roleId === RoleEnum.MZOMAdmin &&
                                <Button outline className={styles.editButton} onClick={() => onAssignAdministratorClick(item.envelopeId)}>
                                    {t("request.assignAdministrator")}
                                </Button>
                            }
                            {item.receivedBy == null && item.programs.some(p => p.isCurrent && (p.statusId === ProgramRequestStatus.SubmittedASOO
                                || p.statusId === ProgramRequestStatus.HarmonizationASOO || p.statusId === ProgramRequestStatus.CancelledASOO || p.statusId === ProgramRequestStatus.SubmittedMZO
                                // || p.statusId === ProgramRequestStatus.HarmonizationMZO || p.statusId === ProgramRequestStatus.ConsentMZO))
                                || p.statusId === ProgramRequestStatus.ConsentMZO || p.statusId === ProgramRequestStatus.CancelledMZO || p.statusId === ProgramRequestStatus.PausedMZOM))
                                && userSession?.roleId === RoleEnum.Helpdesk &&
                                <Button outline className={styles.editButton} onClick={() => onAssignAdministratorClick(item.envelopeId)}>
                                    {t("request.assignAdministrator")}
                                </Button>
                            }
                        </div>
                    )
                },
                sortBy: NISOO2ProgramRequestSort.ReceivedBy
            }
        ],
        rowToolTip: () => undefined
    };

    const programRequestProgramDetailTableMetadata: TableMetadata<ProgramRequestEnvelopeProgram> = {
        rowKey: "programRequestId",
        columns: [
            {
                columnKey: "code",
                header: t("codeBook.programCode"),
                accessor: "code"

            },
            {
                columnKey: "programName",
                header: t("common.programName"),
                accessor: "programName"
            },
            {
                columnKey: "classificationMark",
                header: t("common.classificationMark"),
                accessor: "classificationMark"
            },
            {
                columnKey: "status",
                header: t("status"),
                accessor: (item) => {
                    return (
                        <div className={styles.statusColumn}>
                            <Label color={getProgramRequestStatusColor(item.statusId)}>
                                {item.status}
                            </Label>
                        </div>
                    );
                }
            },
            {
                columnKey: "details",
                header: "",
                accessor: (item) => {
                    return (<div className={"text-right"}>
                        <ArrowButton ariaLabel={t("request.goToRequestDetails")}
                            onClick={() => onOpenRequestDetails(item.programRequestId, item.historyId, item.isCurrent)}
                        />
                    </div>)
                }
            }
        ],
        rowToolTip: () => undefined
    };

    var [masterDetailTableProps, tableState, setfilters] = useMasterDetailTable<NISOO2ProgramRequestListSearch>(
        {
            masterMetadata: programRequestTableMetadata,
            masterData: tableData.results,
            detailMetadata: programRequestProgramDetailTableMetadata,
            detailPropertyColumn: "programs",
            pagination: true,
            paginationPage: initialTableState.page,
            paginationPageSize: initialTableState.pageSize as any,
            totalItems: tableData.rowCount,
            defaultSort: { order: initialTableState.order, sortBy: initialTableState.sortBy }
        }
    );

    const selectableProgramRequestProgramDetailTableMetadata: TableMetadata<SelectableProgramRequestEnvelopeProgram> = {
        rowKey: "programRequestId",
        columns: [
            {
                columnKey: "isSelected",
                header: "",
                accessor: (item: SelectableProgramRequestEnvelopeProgram) => {
                    return (
                        <div>
                            <Checkbox
                                label={""}
                                name={nameof<typeof item>("programRequestId")}
                                value={item.programRequestId}
                                checked={item.isSelected}
                                onChange={handleItemSelectedChanged}
                            />
                        </div>
                    );
                }
            },
            {
                columnKey: "code",
                header: t("codeBook.programCode"),
                accessor: "code"

            },
            {
                columnKey: "programName",
                header: t("common.programName"),
                accessor: "programName"
            },
            {
                columnKey: "classificationMark",
                header: t("common.classificationMark"),
                accessor: "classificationMark"
            },
            {
                columnKey: "status",
                header: t("status"),
                accessor: (item) => {
                    return (
                        <div className={styles.statusColumn}>
                            <Label color={getProgramRequestStatusColor(item.statusId)}>
                                {item.status}
                            </Label>
                        </div>
                    );
                }
            },
        ],
        rowToolTip: () => undefined
    };

    const [selectedProgramsTableProps] = useTable<SelectableProgramRequestEnvelopeProgram>(
        {
            metadata: selectableProgramRequestProgramDetailTableMetadata,
            data: selectedPrograms,
            pagination: false,
            paginationPage: undefined,
            paginationPageSize: undefined,
            totalItems: selectedPrograms.length,
            defaultSort: {}
        }
    );

    useDidUpdateEffect(() => {
        sessionStorage.setItem(storageKey, JSON.stringify({
            ...programRequestSearch,
            sortBy: tableState.sortBy ?? NISOO2ProgramRequestSort.EnvelopeDate,
            order: tableState.order ?? ColumnSortEnum.ASC,
            page: tableState.page ?? 1,
            pageSize: tableState.pageSize ?? 10
        }));
    }, [tableState])

    useEffect(() => {
        if (!userSession) {
            return;
        }

        if (userSession?.roleId === RoleEnum.ASOOOffice || userSession?.roleId === RoleEnum.MZOMOffice || userSession?.roleId === RoleEnum.ASOO
            || userSession?.roleId === RoleEnum.MZOM) {
            setProgramRequestSearch((prev) => ({
                ...prev,
                currentUserProgramRequests: (userSession.roleId === RoleEnum.ASOOOffice || userSession.roleId === RoleEnum.MZOMOffice) ? false : prev.currentUserProgramRequests,
                programRequestsWithoutAdministrator: (userSession.roleId === RoleEnum.ASOO || userSession.roleId === RoleEnum.MZOM
                    || userSession.roleId === RoleEnum.ASOOOffice || userSession.roleId === RoleEnum.MZOMOffice) ? false : prev.programRequestsWithoutAdministrator
            }));
        }

        const searchProgramRequests = async () => {
            const request = convertProgramRequestSearchToApiRequest(programRequestSearch);
            var results = await getProgramRequestListAsync(request);

            setTableData(results);

        };
        searchProgramRequests();

    }, [tableState, userSession]);

    useEffect(() => {
        const getAdministrators = async () => {
            const administrators = await fetchAdministratorsForRole(userSession?.roleId);
            setFilteredAdministrators(administrators);
        };

        if (canFetchAdministrators(userSession?.roleId)) {
            getAdministrators();
        }
    }, [userSession?.roleId]);

    const resolveMasterDetailTableProps = (currentMasterData: ProgramRequestEnvelopeListItem[]) => {
        return { ...masterDetailTableProps, masterData: currentMasterData } as MasterDetailTableProps;
    };

    const onOpenRequestDetails = (programRequestId: number, historyId: number, isCurrent: boolean) => {
        if (isCurrent) {
            history.push(programRequestDetailsUrl(programRequestId), { previousPage: PreviousPage.NISOO2ProgramRequestListPage });
        }
        else {
            history.push(programRequestDetailsUrl(programRequestId), { previousPage: PreviousPage.NISOO2ProgramRequestListPage, historyId: historyId });
        }
    };

    const handleAdministratorIdChange = (e: any) => {
        let administratorId: number | undefined = undefined;
        if (e.target.value) {
            administratorId = Number.parseInt(e.target.value);
        }
        else {
            administratorId = undefined;
            setAdministratorConfirmationMessage("");
        }

        const newAdministratorRequest: AssignAdministratorRequest = {
            ...assignAdministratorRequest,
            administratorId: administratorId
        }
        setAssignAdministratorRequest(newAdministratorRequest);

        if (administratorId !== undefined && administratorId !== null) {
            var selectedAdmin = filteredAdministrators.find(a => a.userId === administratorId);

            if (selectedAdmin) {
                setAdministratorConfirmationMessage(t("request.assignAdministratorConfirmModalMessage", { firstName: selectedAdmin.firstName, lastName: selectedAdmin.lastName }));
            }
        }
    };


    const handleAdministratorSearch = async (search: AutocompleteSearchData) => {

        if (search) {
            const filteredAdmins = filteredAdministrators.filter(a => a.name.toLowerCase().includes(search!.text!.toLowerCase()));
            return {
                valueProp: "userId",
                textProp: "name",
                data: filteredAdmins
            } as SelectData<AppUserRole>
        }
        return {
            valueProp: "userId",
            textProp: "name",
            data: filteredAdministrators
        } as SelectData<AppUserRole>
    };



    return (
        <>
            <Row>
                <Col md={12}>
                    <h2>{t("navigation.newProgramRequestList")}</h2>
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <AutocompleteMultipleSelect<CodeBook>
                        label={t("institution.institutionName")}
                        name={nameof<typeof programRequestSearch>("institutions")}
                        value={programRequestSearch.institutions}
                        valueProp="id"
                        textProp="name"
                        onChange={handleInstitutionChangeV2}
                        selectOptions={filteredInstitutions}
                        onFilterChange={handleInstitutionFilterChange}
                        maximumSelectedItems={1}
                    />
                </Col>
                <Col md={4}>
                    <TextField
                        label={t("request.classificationMark")}
                        name={nameof<typeof programRequestSearch>("classificationMark")}
                        value={programRequestSearch.classificationMark}
                        onChange={handleClassificationMarkChange}
                    />
                </Col>
                <Col md={4}>
                    <Select
                        label={t("common.status")}
                        name={nameof<typeof programRequestSearch>("programRequestStatusId")}
                        value={programRequestSearch.programRequestStatusId}
                        onChange={handleProgramRequestStatusChange}
                        selectData={codeBookToSelectData(programRequestStatuses)}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <TextField
                        label={t("common.programName")}
                        name={nameof<typeof programRequestSearch>("programName")}
                        value={programRequestSearch.programName}
                        onChange={handleProgramNameChange}
                    />
                </Col>
                {/* <Col md={4}>
                    <AutocompleteMultipleSelect<CodeBook>
                        label={t("common.programName")}
                        name={nameof<typeof programRequestSearch>("programs")}
                        value={programRequestSearch.programs} 
                        valueProp="id"
                        textProp="name"
                        onChange={handleProgramChange}
                        selectOptions={filteredPrograms}
                        onFilterChange={handleProgramsFilterChange}
                        maximumSelectedItems={1}
                    />
                </Col> */}
                <Col md={4}>
                    <Select
                        label={t("request.programRequestType")}
                        name={nameof<typeof programRequestSearch>("programRequestTypeIds")}
                        value={programRequestSearch.programRequestTypeIds}
                        onChange={handleProgramRequestTypeChange}
                        selectData={codeBookToSelectData(programRequestTypes)}
                        multiple={true}
                    />
                </Col>
                <Col md={4}>
                    <AutocompleteMultipleSelect<CodeBook>
                        label={t("common.educationSector")}
                        value={programRequestSearch.educationSectors}
                        valueProp="id"
                        textProp="name"
                        onChange={handleEducationSectorChange}
                        selectOptions={filteredEducationSectors}
                        onFilterChange={handleEducationSectorFilterChange}
                        maximumSelectedItems={10}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <TextField
                        label={t("request.administrator")}
                        name={nameof<typeof programRequestSearch>("administrator")}
                        value={programRequestSearch.administrator}
                        onChange={handleAdministratorFilterChange}
                    />
                </Col>
                <Col md={4}>
                    <DatePicker
                        label={t("request.programRequestDateFrom")}
                        name={nameof<typeof programRequestSearch>("dateFrom")}
                        value={programRequestSearch.dateFrom}
                        onChange={handleDateFromFilterChange}
                        minYear={new Date().getFullYear() - 25}
                        maxYear={new Date().getFullYear() + 25}
                    />
                </Col>
                <Col md={4}>
                    <DatePicker
                        label={t("request.programRequestDateTo")}
                        name={nameof<typeof programRequestSearch>("dateTo")}
                        value={programRequestSearch.dateTo}
                        onChange={handleDateToFilterChange}
                        minYear={new Date().getFullYear() - 25}
                        maxYear={new Date().getFullYear() + 25}
                    />
                </Col>
            </Row>
            <Row className="mb-5 mt-5">
                {/* {(userSession?.roleId === RoleEnum.ASOOAdmin || userSession?.roleId === RoleEnum.MZOMAdmin 
                    || userSession?.roleId === RoleEnum.Helpdesk) && 
                <Col md={3}>
                    <Checkbox 
                        label={t("request.showMyProgramRequests")}
                        name={nameof<typeof programRequestSearch>("currentUserProgramRequests")}
                        value={programRequestSearch.currentUserProgramRequests}
                        checked={programRequestSearch.currentUserProgramRequests}
                        onChange={handleCurrentUserProgramRequestChange}
                    />                
                </Col>
                } */}
                <Col md={3}>
                    {(userSession?.roleId === RoleEnum.ASOOAdmin || userSession?.roleId === RoleEnum.MZOMAdmin
                        || userSession?.roleId === RoleEnum.Helpdesk) &&
                        <Checkbox
                            label={t("request.showMyProgramRequests")}
                            name={nameof<typeof programRequestSearch>("currentUserProgramRequests")}
                            value={programRequestSearch.currentUserProgramRequests}
                            checked={programRequestSearch.currentUserProgramRequests}
                            onChange={handleCurrentUserProgramRequestChange}
                        />
                    }
                </Col>
                <Col md={4}>
                    {(userSession?.roleId === RoleEnum.ASOOAdmin || userSession?.roleId === RoleEnum.MZOMAdmin
                        || userSession?.roleId === RoleEnum.Helpdesk) &&
                        <Checkbox
                            label={t("request.showProgramRequestsForProcessing")}
                            name={nameof<typeof programRequestSearch>("programRequestsWithoutAdministrator")}
                            value={programRequestSearch.programRequestsWithoutAdministrator}
                            checked={programRequestSearch.programRequestsWithoutAdministrator}
                            onChange={handleProgramRequestWithoutAdministratorChange}
                        />
                    }
                </Col>
                <Col md={5} className="d-flex justify-content-end align-items-center">
                    <Button onClick={search}>
                        {t("common.search")}
                    </Button>
                    <Typography color="primary" className={styles.clearFilters + " ml-3"} onClick={clearFilters}>
                        {t("common.clearFilters")}
                    </Typography>
                </Col>
            </Row>
            <MasterDetailTable {...resolveMasterDetailTableProps(tableData.results)}></MasterDetailTable>


            <Modal
                type={"confirm"}
                title={t("request.assignAdministratorHeader")}
                variant="big"
                cancelText={t("common.cancel")}
                confirmText={t("request.assignAdministrator")}
                open={isAssignAdministratorModalOpened}
                onClose={handleToggleCancelAssignAdministratorModal}
                onConfirm={handleToggleConfirmAssignAdministratorModal}
            >
                <div>
                    <Row>
                        <Col md={12}>
                            <Typography>{t("request.processingAdministratorModal")}</Typography>
                            <br />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <Autocomplete
                                label={t("request.administrator")}
                                name={nameof<typeof assignAdministratorRequest>("administratorId")}
                                value={assignAdministratorRequest.administratorId}
                                onChange={handleAdministratorIdChange}
                                getOptions={handleAdministratorSearch}
                                errorText={assignAdministratorRequest.administratorId === undefined || assignAdministratorRequest.administratorId === null ? t("validations.required") : ""}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <Typography>{t("request.programsForProcessing")}</Typography>
                            <hr />
                        </Col>
                    </Row>
                    {tableData.results.find(x => x.envelopeId === clickedEnvelopeId)?.programs.map((p) =>
                        <Row>
                            <Col md={12}>
                                <Typography>{p.programName}</Typography>
                            </Col>
                        </Row>
                    )}

                </div>
            </Modal>
            <Modal
                type="confirm"
                open={isConfirmAssignAdministratorModalOpened}
                cancelText={t("common.noProperCase")}
                confirmText={t("request.assignAdministratorConfirm")}
                onClose={handleCancelConfirmAssignAdministratorModal}
                onConfirm={handleConfirmAssignAdministratorModal}
            >
                <Typography component="h1" variant="largeBold">
                    {t("request.assignAdministratorConfirmModal")}
                </Typography>
                <Typography>
                    {administratorConfirmationMessage}
                </Typography>
            </Modal>


            <Modal
                type={"confirm"}
                title={t("request.takeIntoProcessingHeader")}
                variant="big"
                cancelText={t("common.cancel")}
                confirmText={t("request.takeIntoProcessing")}
                open={isTakeIntoProcessingModalOpened}
                onClose={handleToggleCancelTakeIntoProcessingModal}
                onConfirm={handleToggleConfirmTakeIntoProcessingModal}
            >
                <div>
                    <Row>
                        <Col md={12}>
                            <Typography>{t("request.processingClassificationMark")}</Typography>
                            <br />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <TextField
                                label={t("common.classificationMark")}
                                name={nameof<typeof processingRequest>("classificationMark")}
                                value={processingRequest.classificationMark}
                                onChange={handleProcessingClassificationMarkChange}
                                errorText={processingRequest.classificationMark.trim() === "" || processingRequest.classificationMark === undefined ? t("validations.required") : ""}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <Typography>{t("request.programsForProcessing")}</Typography>
                            <hr />
                        </Col>
                    </Row>
                    {tableData.results.find(x => x.envelopeId === clickedEnvelopeId)?.programs.map((p) =>
                        <Row>
                            <Col md={12}>
                                <Typography>{p.programName}</Typography>
                            </Col>
                        </Row>

                    )}
                </div>

            </Modal>

            <Modal
                type={"confirm"}
                variant="big"
                cancelText={t("common.cancel")}
                confirmText={resolutionDocument.documentTypeId === ResolutionDocumentType.InstitutionBasicDataChangeResolution ? t("request.insertResolution") : t("request.judgeRequest")}
                open={isGroupOpinionModalOpened}
                onClose={handleToggleCancelIsGroupOpinionModal}
                onConfirm={handleToggleConfirmIsGroupOpinionModal}
            >
                <div className={styles.modalPanel}>
                    <Typography component="h1" variant="largeBold" className={styles.modalCaption}>
                        {resolutionDocument.documentTypeId === ResolutionDocumentType.InstitutionBasicDataChangeResolution
                            ? t("request.executionRequest") : t("request.judgeRequestModalCaption")}
                    </Typography>
                    <div className={styles.modalTitle}>
                        {resolutionDocument.documentTypeId === ResolutionDocumentType.InstitutionBasicDataChangeResolution
                            ? t("request.groupResolutionProgramSelectTitle") : t("request.groupOpinionProgramSelectTitle")}
                    </div>
                </div>
                <Table {...selectedProgramsTableProps} />
            </Modal>

            <Modal
                type={"message"}
                confirmText={t("common.ok")}
                open={isGroupOpinionValidationModalOpened}
                onClose={handleGroupOpinionValidationModalClose}
                onConfirm={handleGroupOpinionValidationModalClose}
            >
                <div>
                    <Typography>{t("request.groupOpinionProgramSelectValidation")}</Typography>
                </div>
            </Modal>

            <ModalResolution
                type={"confirm"}
                open={openResolutionModal}
                cancelText={t("common.cancel")}
                confirmText={t("request.judgeRequestModalConfirm")}
                rejectText={t("request.judgeRequestModalReject")}
                onClose={handleCloseResolutionModal}
                onConfirm={(approved, resolution) => handleConfirmedResolutionModal(approved, resolution)}
                showComment={true}
                forceComment={false}
                documentToolTip={t("document.pdfTooltip")}
                documentFilter={t("document.pdfFilter")}
                captions={getResolutionModalCaptions(resolutionDocument.documentTypeId)}
                executionDocumentData={resolutionDocument}
                resolutionDoc={resolutionDocument}
            >
                <div className={styles.modalPanel}>
                    <Typography component="h1" variant="largeBold" className={styles.modalCaption}>
                        {resolutionDocument.documentTypeId === ResolutionDocumentType.InstitutionBasicDataChangeResolution
                            ? t("request.executionRequest") : t("request.judgeRequestModalCaption")}
                    </Typography>
                    <div className={styles.modalTitle}>
                        {resolutionDocument.documentTypeId === ResolutionDocumentType.InstitutionBasicDataChangeResolution
                            ? t("request.executionResolutionModalTitle") : t("request.judgeRequestModalTitle")}
                    </div>
                </div>

            </ModalResolution>

            <Modal
                type="message"
                open={openStatusChangeModal}
                confirmText={t("common.ok")}
                onClose={handleStatusChangeMessageModal}
                onConfirm={handleStatusChangeMessageModal}
            >
                <div className={styles.changeStatusMessage}>
                    <div className={styles.statusMessageIconPanel}>
                        <IconWrapper color="noted-2">
                            <DocumentApprovedIcon aria-label={t("accessibility.documentApprovedIcon")} />
                        </IconWrapper>
                    </div>
                    <Typography variant="largeBold" component="h3">
                        {confirmationMessage}
                    </Typography>
                </div>

            </Modal>
            <Modal
                type={"confirm"}
                title={t("request.classificationMarkRegistryNumberEditModalHeader")}
                variant="big"
                cancelText={t("common.cancel")}
                confirmText={t("common.save")}
                open={isClassificationMarkRegistryNumberEditModalOpened}
                onClose={handleToggleCancelClassificationMarkRegistryNumberEditModal}
                onConfirm={handleToggleConfirmClassificationMarkRegistryNumberEditModal}
            >
                <div>
                    <Row>
                        <Col md={12}>
                            <Typography>{t("request.classificationMarkRegistryNumberEditTooltip")}</Typography>
                            <br />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <TextField
                                label={t("common.classificationMark")}
                                name={nameof<typeof envelopeClassificationRequest>("classificationMark")}
                                value={envelopeClassificationRequest.classificationMark}
                                onChange={handleEnvelopeClassificationMarkChange}
                                errorText={!envelopeClassificationRequest.classificationMark ? t("validations.required") : ""}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <TextField
                                label={t("request.resolutionRegisterNumber")}
                                name={nameof<typeof envelopeClassificationRequest>("registryNumber")}
                                value={envelopeClassificationRequest.registryNumber}
                                onChange={handleEnvelopeRegistryNumberChange}
                                errorText={!envelopeClassificationRequest.registryNumber ? t("validations.required") : ""}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <DatePicker
                                label={t("request.committeeMembershipIssueDate")}
                                name={nameof<typeof envelopeClassificationRequest>("registrationDate")}
                                value={envelopeClassificationRequest.registrationDate}
                                onChange={handleEnvelopeRegistrationDateChange}
                                isClearable={true}
                                minYear={new Date().getFullYear() - 25}
                                maxYear={new Date().getFullYear() + 25}
                                showTimePicker={false}
                                errorText={!envelopeClassificationRequest.registrationDate ? t("validations.required") : ""}
                            />
                        </Col>
                    </Row>
                </div>
            </Modal>
            <Modal
                type="confirm"
                open={confirmClassificationMarkEditModalOpened}
                cancelText={t("common.close")}
                confirmText={t("common.save")}
                onClose={handleConfirmModalClose}
                onConfirm={handleConfirmModalConfirm}
            >
                <div>
                    <Typography component="h1" variant="largeBold">
                        {t("common.areYouSureSaveChanges")}
                    </Typography>
                </div>
            </Modal>
            <Modal
                type="message"
                open={openSuccessModal}
                confirmText={t("common.close")}
                onClose={handleCloseSuccessModal}
                onConfirm={handleCloseSuccessModal}
            >
                <div className={styles.modalMessageContentAlignCenter}>
                    <Typography component="h1" variant="largeBold">
                        {t("common.saveSucceed")}
                    </Typography>
                </div>
            </Modal>
            <Modal
                type="confirm"
                open={openResolutionConfirmModal}
                cancelText={t("common.noProperCase")}
                confirmText={t("common.yesProperCase")}
                onClose={handleCancelResolutionConfirmModal}
                onConfirm={handleConfirmResolutionConfirmModal}
            >
                <div>
                    <Typography component="h1" variant="largeBold">
                        {confirmationModalMessage}
                    </Typography>
                </div>

            </Modal>

        </>
    );
}